// Legacy design exports retained for existing Overview, Reference and Cascade consumers.
// Calculator Pass 1 does not consume these constants or execute design scoring.
import type { MackBlockKey } from './blocks';
export interface MackRatingOption { label: string; score: number }

export interface MackCriterion {
  key: string;
  name: string;
  block: MackBlockKey;
  rating: string;                 // the sheet's stored rating (the initial selection)
  max: number;
  scoring_logic: string;
  age: string;
  options: MackRatingOption[];
  // DELIBERATELY NO `score` FIELD. The brief's shape lists one, but score is
  // fully determined by (rating × options) — storing both lets them disagree,
  // and a stale score is worse than no score. Forge's store may still carry it;
  // the component recomputes and ignores it, so the two can never desync.
}

export interface MackCalcSheet {
  sheet_code: string;
  display_label: string;
  stage: string;
  delta_growth: string;           // '+' | '−' | '0'
  delta_inflation: string;
  next_catalyst: string;
  // OWNER RULING (build-01, extended build-03→04): activation renders the
  // honest STORE value, not the design's live-looking figure. All six sheets
  // sit at the 0.15 floor so Calculator and Calendar agree.
  activation: number;
  reactive: boolean;              // REACTIVE vs SLEEPING
  // The environment reads "FROM PHASE, OVERRIDABLE". The design gives one
  // sample (Reflation → Inflation Wk), which is not enough to derive a
  // phase→environment rule, so it is stored per sheet rather than inferred.
  default_environment: string;
  from_design: boolean;
  criteria_macro: MackCriterion[];
  criteria_sentiment: MackCriterion[];
}

export interface MackEnvironment {
  key: string;
  label: string;
  macro_weight: number;
  trigger_note: string;
}

export interface MackTier {
  name: string;
  M: number;
  // 0–3 per block. DESIGN DATA, SOLVED BACKWARDS: the matrix below was derived
  // by fitting each tier's sensitivities so B·F reproduces the five values the
  // design displays for the JPY vector (0.71 / 0.60 / 0.58 / 0.56 / 0.53) AND
  // their rank order. It is therefore consistent with the design but is NOT
  // authoritative — the store will own the real matrix. FORGE: replace wholesale.
  sensitivity: Record<MackBlockKey, number>;
  // "top five with M ≥ 0.15 and non-zero directional weight" — the directional
  // filter is named in the spec but not visible in the design, so it is modelled
  // as its own field. Market Makers sit at 0 (non-directional by nature) to
  // exercise it; Retail sits below the M floor to exercise that.
  directional_weight: number;
}

// ── ENVIRONMENTS ─────────────────────────────────────────────────────────────
// Weights are specified. Only Inflation Wk's trigger note is legible in the
// design — the other four are written to match its register and are INVENTED.
export const MACK_CALC_ENVIRONMENTS: MackEnvironment[] = [
  { key: 'stable',       label: 'Stable',       macro_weight: 0.70,
    trigger_note: 'No scheduled catalyst inside the window. Fundamentals carry the read.' },
  { key: 'inflation_wk', label: 'Inflation Wk', macro_weight: 0.80,
    trigger_note: 'CPI, PCE or rate decision inside the window. The print dominates.' },
  { key: 'reversal',     label: 'Reversal',     macro_weight: 0.50,
    trigger_note: 'Positioning stretched against the fundamental read. Both sides count equally.' },
  { key: 'panic',        label: 'Panic',        macro_weight: 0.30,
    trigger_note: 'Correlations collapse to one. Flow overwhelms fundamentals.' },
  { key: 'euphoria',     label: 'Euphoria',     macro_weight: 0.40,
    trigger_note: 'Risk bid with no fundamental support. Sentiment leads, fundamentals lag.' },
];

// ── TIER SENSITIVITY MATRIX ──────────────────────────────────────────────────
// Eleven tiers. See MackTier.sensitivity — solved backwards from the design,
// pending Forge's authoritative version.
const S = (P: number, Pr: number, G: number, E: number, Po: number, R: number): Record<MackBlockKey, number> =>
  ({ Policy: P, Prices: Pr, Growth: G, External: E, Positioning: Po, Risk: R });

export const MACK_CALC_TIERS: MackTier[] = [
  { name: 'Corporates',        M: 0.88, sensitivity: S(1, 3, 1, 2, 0, 1), directional_weight: 1 },
  { name: 'Central Banks',     M: 0.90, sensitivity: S(3, 3, 2, 2, 0, 1), directional_weight: 1 },
  { name: 'Pensions / Family', M: 0.82, sensitivity: S(2, 2, 2, 2, 1, 1), directional_weight: 1 },
  { name: 'Institutional',     M: 0.84, sensitivity: S(2, 2, 2, 2, 2, 1), directional_weight: 1 },
  { name: 'Large Banks',       M: 0.76, sensitivity: S(3, 2, 2, 2, 2, 2), directional_weight: 1 },
  { name: 'Hedge Funds',       M: 0.72, sensitivity: S(1, 1, 1, 1, 3, 3), directional_weight: 1 },
  { name: 'CTAs / Systematic', M: 0.64, sensitivity: S(0, 1, 1, 1, 3, 3), directional_weight: 1 },
  { name: 'Sovereign Wealth',  M: 0.55, sensitivity: S(2, 2, 2, 3, 1, 1), directional_weight: 1 },
  { name: 'Market Makers',     M: 0.58, sensitivity: S(0, 0, 0, 1, 2, 3), directional_weight: 0 },
  { name: 'Prop / HFT',        M: 0.34, sensitivity: S(0, 0, 0, 0, 2, 3), directional_weight: 1 },
  { name: 'Retail',            M: 0.12, sensitivity: S(0, 1, 1, 0, 3, 3), directional_weight: 1 },
];

// ── CRITERIA TEMPLATES ───────────────────────────────────────────────────────
// The criteria SET is the framework and is identical across sheets; only the
// ratings and ages differ per sheet. Built into per-sheet arrays below so the
// exported shape still matches the contract, without six copies of the logic.
//
// OPTION LISTS ARE INFERRED. A closed <select> shows one value, so the design
// only ever reveals the CURRENT rating. Each option set is reconstructed from
// its scoring-logic string; the selected label and its score are verbatim.
type CriterionTemplate = Omit<MackCriterion, 'rating' | 'age'>;

const O = (...pairs: [string, number][]): MackRatingOption[] =>
  pairs.map(([label, score]) => ({ label, score }));

const MACRO_TEMPLATE: CriterionTemplate[] = [
  { key: 'rate_level', name: 'Interest Rate Level', block: 'Policy', max: 3,
    scoring_logic: '<1 Low · 1–3 Mod · 3–5 High · >5 VH',
    options: O(['Low', 0], ['Moderate', 1], ['High', 2], ['Very High', 3]) },
  { key: 'rate_trend', name: 'Interest Rate Trend', block: 'Policy', max: 2,
    // NOTE: the logic string says "Steady 1" but the select reads "Neutral".
    // The select's vocabulary is what renders, so it wins; the logic string is
    // transcribed verbatim including the mismatch.
    scoring_logic: 'Cutting 0 · Steady 1 · Hiking 2',
    options: O(['Cutting', 0], ['Neutral', 1], ['Hiking', 2]) },
  { key: 'inflation', name: 'Inflation (YoY %)', block: 'Prices', max: 2,
    scoring_logic: '<2 Low · 2–4 Mod · >4 High',
    options: O(['Low', 0], ['Moderate', 1], ['High', 2]) },
  { key: 'gdp_trend', name: 'GDP Growth Trend', block: 'Growth', max: 2,
    scoring_logic: '<1 Poor · 1–2.5 Okay · >2.5 Strong',
    options: O(['Poor', 0], ['Okay', 1], ['Strong', 2]) },
  { key: 'trade_balance', name: 'Trade Balance', block: 'External', max: 2,
    scoring_logic: 'Surplus >2% GDP exports',
    options: O(['Deficit', 0], ['Balanced', 1], ['Surplus', 2]) },
  { key: 'capital_flow', name: 'Capital Flow Attraction', block: 'External', max: 2,
    scoring_logic: 'Strong ⇒ $100B+/mo inflows',
    options: O(['Weak', 0], ['Moderate', 1], ['Strong', 2]) },
  { key: 'cot_bias', name: 'COT Positioning Bias', block: 'Positioning', max: 2,
    scoring_logic: '±50K neutral',
    options: O(['Net Short', 0], ['Neutral', 1], ['Net Long', 2]) },
];

const SENTIMENT_TEMPLATE: CriterionTemplate[] = [
  { key: 'equity_sent', name: 'Equity Sentiment', block: 'Risk', max: 2,
    scoring_logic: 'Bearish = equities down',
    options: O(['Bearish', 0], ['Neutral', 1], ['Bullish', 2]) },
  { key: 'bond_yield_sent', name: 'Bond Yield Sentiment', block: 'Positioning', max: 2,
    scoring_logic: 'Bullish = rising yields',
    options: O(['Bearish', 0], ['Neutral', 1], ['Bullish', 2]) },
  { key: 'volatility', name: 'Volatility (VIX/OVX)', block: 'Risk', max: 2,
    scoring_logic: 'Bullish = VIX below 15',
    options: O(['Bearish', 0], ['Neutral', 1], ['Bullish', 2]) },
  { key: 'retail_pos', name: 'Retail Positioning', block: 'Positioning', max: 2,
    // INVERTED by design — retail long scores 0. The option order encodes it.
    scoring_logic: 'Inverted — retail long scores 0',
    options: O(['Net Long', 0], ['Neutral', 1], ['Net Short', 2]) },
  { key: 'cot_reversal', name: 'COT Reversal', block: 'Positioning', max: 2,
    scoring_logic: 'Bullish = squeeze setup',
    options: O(['Bearish', 0], ['Neutral', 1], ['Bullish', 2]) },
  { key: 'global_risk', name: 'Global Risk', block: 'Risk', max: 2,
    scoring_logic: 'Bearish = geopolitical tension',
    options: O(['Bearish', 0], ['Neutral', 1], ['Bullish', 2]) },
  { key: 'news_sent', name: 'News Sentiment', block: 'Risk', max: 2,
    scoring_logic: 'Headlines risk-on/off',
    options: O(['Bearish', 0], ['Neutral', 1], ['Bullish', 2]) },
];

function buildCriteria(
  tpl: CriterionTemplate[],
  ratings: Record<string, string>,
  ages: Record<string, string>,
): MackCriterion[] {
  return tpl.map(t => ({ ...t, rating: ratings[t.key], age: ages[t.key] }));
}

// ── SHEETS ───────────────────────────────────────────────────────────────────
// JPY is transcribed VERBATIM from the design — every rating, every age, the
// stage line, the activation, the environment. The other five are EXTRAPOLATED.
export const MACK_CALC_SHEETS: MackCalcSheet[] = [
  {
    sheet_code: 'JPY', display_label: 'JPY', stage: 'Reflation',
    delta_growth: '+', delta_inflation: '+',
    // The design's state line reads "… · next BoJ · 9d · CPI landed · A 1.00".
    // "CPI landed" is a resolved-event note rather than part of the catalyst;
    // with the given shape it folds into next_catalyst so the line renders
    // exactly. Forge may want it as its own field — flagged.
    next_catalyst: 'next BoJ · 9d · CPI landed',
    activation: 0.15, reactive: true,
    default_environment: 'inflation_wk', from_design: true,
    criteria_macro: buildCriteria(MACRO_TEMPLATE, {
      rate_level: 'Low', rate_trend: 'Neutral', inflation: 'High', gdp_trend: 'Okay',
      trade_balance: 'Surplus', capital_flow: 'Moderate', cot_bias: 'Net Short',
    }, {
      rate_level: '2m', rate_trend: '2m', inflation: '12d', gdp_trend: '18h',
      trade_balance: '25d', capital_flow: '5d', cot_bias: '3d',
    }),
    criteria_sentiment: buildCriteria(SENTIMENT_TEMPLATE, {
      equity_sent: 'Neutral', bond_yield_sent: 'Bearish', volatility: 'Neutral',
      retail_pos: 'Neutral', cot_reversal: 'Bullish', global_risk: 'Bearish',
      news_sent: 'Bullish',
    }, {
      equity_sent: '1h', bond_yield_sent: '1h', volatility: '1h', retail_pos: '24h',
      cot_reversal: '3d', global_risk: '6h', news_sent: '40m',
    }),
  },

  {
    sheet_code: 'USD', display_label: 'US', stage: 'Late cycle',
    delta_growth: '0', delta_inflation: '−', next_catalyst: 'next FOMC · 21d',
    activation: 0.15, reactive: false,
    default_environment: 'stable', from_design: false,
    criteria_macro: buildCriteria(MACRO_TEMPLATE, {
      rate_level: 'High', rate_trend: 'Cutting', inflation: 'Moderate', gdp_trend: 'Strong',
      trade_balance: 'Deficit', capital_flow: 'Strong', cot_bias: 'Net Long',
    }, {
      rate_level: '2m', rate_trend: '2m', inflation: '12d', gdp_trend: '18h',
      trade_balance: '25d', capital_flow: '5d', cot_bias: '3d',
    }),
    criteria_sentiment: buildCriteria(SENTIMENT_TEMPLATE, {
      equity_sent: 'Bullish', bond_yield_sent: 'Neutral', volatility: 'Bullish',
      retail_pos: 'Net Long', cot_reversal: 'Neutral', global_risk: 'Neutral',
      news_sent: 'Bullish',
    }, {
      equity_sent: '1h', bond_yield_sent: '1h', volatility: '1h', retail_pos: '24h',
      cot_reversal: '3d', global_risk: '6h', news_sent: '40m',
    }),
  },

  {
    sheet_code: 'GBP', display_label: 'UK', stage: 'Disinflation',
    delta_growth: '−', delta_inflation: '−', next_catalyst: 'next CPI · 12d',
    activation: 0.15, reactive: false,
    default_environment: 'stable', from_design: false,
    criteria_macro: buildCriteria(MACRO_TEMPLATE, {
      rate_level: 'High', rate_trend: 'Cutting', inflation: 'Moderate', gdp_trend: 'Poor',
      trade_balance: 'Deficit', capital_flow: 'Moderate', cot_bias: 'Neutral',
    }, {
      rate_level: '6d', rate_trend: '6d', inflation: '9d', gdp_trend: '14d',
      trade_balance: '31d', capital_flow: '8d', cot_bias: '3d',
    }),
    criteria_sentiment: buildCriteria(SENTIMENT_TEMPLATE, {
      equity_sent: 'Neutral', bond_yield_sent: 'Bearish', volatility: 'Neutral',
      retail_pos: 'Neutral', cot_reversal: 'Neutral', global_risk: 'Bearish',
      news_sent: 'Neutral',
    }, {
      equity_sent: '2h', bond_yield_sent: '2h', volatility: '2h', retail_pos: '24h',
      cot_reversal: '3d', global_risk: '6h', news_sent: '1h',
    }),
  },

  {
    sheet_code: 'EUR', display_label: 'EUR', stage: 'Stall',
    delta_growth: '−', delta_inflation: '0', next_catalyst: 'next ECB · 6d',
    activation: 0.15, reactive: true,
    default_environment: 'reversal', from_design: false,
    criteria_macro: buildCriteria(MACRO_TEMPLATE, {
      rate_level: 'Moderate', rate_trend: 'Cutting', inflation: 'Low', gdp_trend: 'Poor',
      trade_balance: 'Surplus', capital_flow: 'Weak', cot_bias: 'Neutral',
    }, {
      rate_level: '19d', rate_trend: '19d', inflation: '8d', gdp_trend: '26d',
      trade_balance: '30d', capital_flow: '7d', cot_bias: '3d',
    }),
    criteria_sentiment: buildCriteria(SENTIMENT_TEMPLATE, {
      equity_sent: 'Neutral', bond_yield_sent: 'Neutral', volatility: 'Neutral',
      retail_pos: 'Neutral', cot_reversal: 'Bearish', global_risk: 'Bearish',
      news_sent: 'Bearish',
    }, {
      equity_sent: '1h', bond_yield_sent: '1h', volatility: '1h', retail_pos: '24h',
      cot_reversal: '3d', global_risk: '6h', news_sent: '55m',
    }),
  },

  {
    sheet_code: 'AUD', display_label: 'AUD', stage: 'Terms-of-trade lift',
    delta_growth: '+', delta_inflation: '0', next_catalyst: 'next RBA · 14d',
    activation: 0.15, reactive: false,
    default_environment: 'stable', from_design: false,
    criteria_macro: buildCriteria(MACRO_TEMPLATE, {
      rate_level: 'High', rate_trend: 'Neutral', inflation: 'Moderate', gdp_trend: 'Okay',
      trade_balance: 'Surplus', capital_flow: 'Moderate', cot_bias: 'Net Short',
    }, {
      rate_level: '13d', rate_trend: '13d', inflation: '5d', gdp_trend: '22d',
      trade_balance: '18d', capital_flow: '9d', cot_bias: '3d',
    }),
    criteria_sentiment: buildCriteria(SENTIMENT_TEMPLATE, {
      equity_sent: 'Neutral', bond_yield_sent: 'Neutral', volatility: 'Neutral',
      retail_pos: 'Net Short', cot_reversal: 'Bullish', global_risk: 'Neutral',
      news_sent: 'Neutral',
    }, {
      equity_sent: '1h', bond_yield_sent: '1h', volatility: '1h', retail_pos: '24h',
      cot_reversal: '3d', global_risk: '6h', news_sent: '2h',
    }),
  },

  {
    sheet_code: 'BLOC', display_label: 'Reserve bloc', stage: 'Accumulation',
    delta_growth: '0', delta_inflation: '0', next_catalyst: 'physical demand · 3d',
    activation: 0.15, reactive: true,
    default_environment: 'euphoria', from_design: false,
    criteria_macro: buildCriteria(MACRO_TEMPLATE, {
      rate_level: 'Moderate', rate_trend: 'Neutral', inflation: 'Moderate', gdp_trend: 'Okay',
      trade_balance: 'Surplus', capital_flow: 'Strong', cot_bias: 'Net Long',
    }, {
      rate_level: '34d', rate_trend: '34d', inflation: '30d', gdp_trend: '45d',
      trade_balance: '61d', capital_flow: '7d', cot_bias: '3d',
    }),
    criteria_sentiment: buildCriteria(SENTIMENT_TEMPLATE, {
      equity_sent: 'Neutral', bond_yield_sent: 'Neutral', volatility: 'Neutral',
      retail_pos: 'Neutral', cot_reversal: 'Bullish', global_risk: 'Bearish',
      news_sent: 'Neutral',
    }, {
      equity_sent: '18h', bond_yield_sent: '18h', volatility: '18h', retail_pos: '24h',
      cot_reversal: '3d', global_risk: '6h', news_sent: '4h',
    }),
  },
];
